import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BellRing, Youtube } from "lucide-react";
import { toast } from "sonner";
import { errMsg, useApi, loadFailed } from "../../lib/api";

export const YouTubeConnect = () => {
  const api = useApi();
  const [s, setS] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = useCallback(() => api.get("/youtube/status").then((r) => setS(r.data))
    .catch((e) => toast.error(errMsg(e, "Couldn't check the YouTube connection"))), [api]);
  useEffect(() => { load(); }, [load]);
  const ytResult = params.get("youtube");
  useEffect(() => {
    if (!ytResult) return;
    if (ytResult === "connected") toast.success("YouTube channel connected");
    else toast.error("YouTube connection didn't finish");
    setParams({}, { replace: true });
  }, [ytResult, setParams]);
  const connect = async () => {
    try { const { data } = await api.get("/youtube/connect", { params: { origin: window.location.origin } }); window.location.href = data.url; }
    catch (e) { toast.error(errMsg(e)); }
  };
  const disconnect = async () => { await api.post("/youtube/disconnect"); toast.success("YouTube disconnected"); load(); };
  return (
    <div className="panel p-5 space-y-3" data-testid="youtube-connect-card">
      <div className="flex items-center gap-2"><Youtube className="h-4 w-4 text-pink-400" /><p className="font-bold">YouTube channel</p></div>
      {!s ? <p className="text-sm text-zinc-500">Checking…</p> : !s.configured ? (
        <p className="text-sm text-zinc-400" data-testid="youtube-status">Waiting for the YouTube keys from your Google developer project. Once they're added you can connect the channel here.</p>
      ) : s.connected ? (
        <div className="flex flex-wrap items-center gap-3" data-testid="youtube-status">
          <span className="h-2 w-2 rounded-full bg-[#39ff6a] glow-green" /><span className="text-sm">Connected to <b>{s.channel_title}</b></span>
          <button onClick={disconnect} data-testid="youtube-disconnect-btn" className="ml-auto text-xs text-zinc-400 hover:text-pink-300">Disconnect</button>
        </div>
      ) : (
        <button onClick={connect} data-testid="youtube-connect-btn" className="rounded-[3px] bg-pink-500 px-4 py-2 text-sm font-bold text-white hover:bg-pink-400">Connect YouTube channel</button>
      )}
      <p className="mono text-[10px] text-zinc-500">Publish recordings from the Music Library as Unlisted or Private.</p>
    </div>
  );
};

export const ReminderSetting = () => {
  const api = useApi();
  const [mode, setMode] = useState(null);
  useEffect(() => { api.get("/admin/reminders").then((r) => setMode(r.data.reminder_mode)).catch(loadFailed("reminder settings")); }, [api]);
  const save = async (m) => {
    try { await api.patch("/admin/reminders", { reminder_mode: m }); setMode(m); toast.success("Reminder timing saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5 space-y-3" data-testid="reminder-setting-card">
      <div className="flex items-center gap-2"><BellRing className="h-4 w-4 text-yellow-400" /><p className="font-bold">Show reminder emails</p></div>
      <p className="text-sm text-zinc-400">Ticket holders get an email before their show starts.</p>
      <div className="flex gap-2">
        {[["both", "24 hours + 1 hour before"], ["1h", "1 hour before only"]].map(([v, l]) => (
          <button key={v} onClick={() => save(v)} data-testid={`reminder-mode-${v}`}
            className={`flex-1 rounded-[3px] border px-3 py-2 text-sm transition-colors ${mode === v ? "border-yellow-400 bg-yellow-400/10 text-yellow-200" : "border-zinc-800 text-zinc-300 hover:border-zinc-600"}`}>{l}</button>
        ))}
      </div>
    </div>
  );
};
